import useAddOns from 'Hooks/useAddOns';
import React, { ReactNode, useEffect } from 'react';

import { useNavigate } from 'react-router-dom';
import {
	clearFerryBookingTransactionIdFromStorage,
	getFerryBookingTransactionIdFromStorage,
} from 'Services/Api/_HumanWritten/BookingService/BookingService';
import classNames from 'classnames';
import { ScrollToError } from 'Validators/Functions/HumanWritten/ScrollToError';
import { SelectedTrips } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardWrap';
import { FerryBookingWizardBottomBar } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BottomBar/FerryBookingWizardBottomBar';
import { FerryBookingWizardStepContent } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/Components/FerryBookingWizardStepContent';
import { FerryBookingWizardProgressBar } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/ProgressBar/FerryBookingWizardProgressBar';
import { BookingWizardSidebar } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/Sidebar/BookingWizardSidebar';
import { AuthStep } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/AuthStep';
import If from 'Views/Components/If/If';
import { stringIsEmpty } from 'Util/TypeGuards';
import { pushObservabilityEvent } from 'Util/_HumanWritten/Observability/observability';
import { clearBookingWizardData, useBookingWizardDataStore } from './BookingWizardDataStore';
import { BookingWizardData } from './BookingWizardData/BaseBookingWizardData';
import { ScrollToTopOfElement } from 'Validators/Functions/HumanWritten/ScrollToElement';
import { acknowledgeMarketingPreference } from 'Services/Api/AccountService';
import { RestrictedModeBanner } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/Components/RestrictedModeBanner';

export interface BookingWizardProps {
	selectedTrips: SelectedTrips;
	tabs: BookingWizardTab[];
	baseUrl: string;
	isMobileBookingWizard: boolean;
	disableBackAndContinueButtons: boolean;
	disableContinueButton: boolean;
	tabIsLoading: boolean;
	setDisableContinueButton: (disableContinueButton: boolean) => void;
	paymentProcessing: boolean;
}

export enum VisibilityStatus {
	VISIBLE = 'VISIBLE',
	HIDDEN = 'HIDDEN',
	EXCLUDED = 'EXCLUDED',
}

export enum SidebarStatus {
	FILTERS,
	CART_SUMMARY,
	NO_SIDEBAR,
}

export interface BookingWizardTab {
	name: string;
	displayName: string;
	path: string;
	visibility: VisibilityStatus; // If true, the step will be skipped
	sidebarStatus: SidebarStatus; // If true, show the ferry filters. If false, show the cart summary. not required for mobile
	validateStep: (updateErrorState: boolean) => boolean;
	component: ReactNode;
	authorize: boolean;
	className?: string;
	contentClass?: string;
	showTAndCsButton: boolean;
	hideBottomBar?: boolean;
	continueBtnText?: string;
	continueBtnLoadingText?: string;
	continueBtnProps?: React.ButtonHTMLAttributes<Element>;
	tabTitle: string;
	tabTitleEdit?: string;
	tabIsLoading?: boolean;
	disableContinueButton?: boolean;
	/**
	 * When defined, the checkbox in the bottom bar will have its state manually controlled.
	 */
	checkboxProps?: {
		checked: boolean;
		toggle: () => void;
	};
	isLinkDisabled?: (currentTab: BookingWizardTab) => boolean;
}

export function BookingWizard({
	selectedTrips,
	tabs,
	baseUrl,
	isMobileBookingWizard,
	disableBackAndContinueButtons,
	disableContinueButton,
	tabIsLoading,
	setDisableContinueButton,
	paymentProcessing,
}: BookingWizardProps) {
	const { wizardData, setWizardData, setOldWizardData, clearOldWizardData } = useBookingWizardDataStore();

	const navigate = useNavigate();

	useAddOns(wizardData.userId, false);
	const pathObjects = window.location.pathname.split('/');
	let currentTabIndex: number = -1;
	const bookingForAlteration = wizardData.wizardMode === 'ALTERATION';

	useEffect(() => {
		if (stringIsEmpty(wizardData.userId) && getFerryBookingTransactionIdFromStorage() !== null) {
			clearFerryBookingTransactionIdFromStorage();
		}
	}, []);

	// Every step change funnels through this component's render (the active step is the URL
	// segment), so this is the single central breadcrumb point for wizard progression.
	const currentStepPath = pathObjects.length === 3 ? pathObjects[2].toLowerCase() : undefined;
	useEffect(() => {
		if (currentStepPath) {
			pushObservabilityEvent('wizard_step', { step: currentStepPath });
		}
	}, [currentStepPath]);
	const setDocTitle = (tabToRender: BookingWizardTab, editMode?: boolean) => {
		if (editMode && tabToRender.tabTitleEdit !== undefined) {
			document.title = tabToRender.tabTitleEdit;
		} else {
			document.title = tabToRender.tabTitle;
		}
	};
	const progressBarClass = 'booking-wizard-progress-bar';

	if (pathObjects.length === 2) {
		const firstTab = tabs.find(tab => tab.visibility !== VisibilityStatus.EXCLUDED);
		navigate(!!firstTab ? `${baseUrl}/${firstTab.path}` : '/404', { replace: true });
		return <></>;
	}

	if (pathObjects.length === 3) {
		currentTabIndex = tabs.findIndex(tab => tab.path.toLowerCase() === pathObjects[2].toLowerCase());

		if (currentTabIndex === -1) {
			navigate('/booking-wizard');
			return <></>;
		}

		// This snippet is for if we land on an excluded page. upon landing on an excluded page we should navigate
		// forward to the next visible or hidden page. The reason for this logic is that if we are on a visible/hidden
		// page which changes to excluded, without this snippet we would be navigated to the 404 page immediately
		if (tabs[currentTabIndex].visibility === VisibilityStatus.EXCLUDED) {
			for (let i = currentTabIndex + 1; i < tabs.length; i++) {
				if (tabs[i].visibility !== VisibilityStatus.EXCLUDED) {
					navigate(`${baseUrl}/${tabs[i].path}`);
					return <></>;
				}
			}

			// At this point, we will have not found a valid step to navigate to when the tabs visibility is excluded.
			// If that is the case, then we will let the user be navigated to 404
			currentTabIndex = -1;
		}

		// This snippet will ensure that if the user lands on a page in the wizard and they haven't completed the
		// previous step, then they will be returned to the earliest step that they haven't completed
		for (let i = 0; i < currentTabIndex; i++) {
			if (tabs[i].visibility !== VisibilityStatus.EXCLUDED && !tabs[i].validateStep(false)) {
				navigate(`${baseUrl}/${tabs[i].path}`);
				return <></>;
			}
		}
	}

	// We don't need to do any other checks here, because it the path objects length isn't 3, then tab index won't be
	// reassigned from its initial value of -1
	if (currentTabIndex === -1) {
		navigate('/404');
		return <></>;
	}

	const tabToRender = tabs[currentTabIndex];

	setDocTitle(tabToRender, bookingForAlteration);

	const timerShown = getFerryBookingTransactionIdFromStorage() !== null;
	const containerClass =
		timerShown && isMobileBookingWizard
			? 'booking-wizard-step-content-container booking-wizard-mobile-timer-padding'
			: 'booking-wizard-step-content-container';
	const contentClass = isMobileBookingWizard ? ['booking-wizard-step-content-no-top-gap-mobile'] : []; // default must be undefined as there are default classes in FerryBookingWizardStepContent

	if (!!tabToRender.contentClass) {
		contentClass.push(tabToRender.contentClass);
	}

	const onTimerExpiry = () => {
		const newData = new BookingWizardData({ ...wizardData });
		newData.departureTicketId = '';
		newData.returningTicketId = '';
		newData.bookingToEdit = '';

		newData.departingJourney = undefined;
		newData.returningJourney = undefined;

		clearOldWizardData();
		setWizardData(newData);
	};

	const progressBarLinksDisabled = shouldDisableProgressBarLinks(disableBackAndContinueButtons, tabToRender);

	const bookingWizardStep = (
		<div className="booking-wizard-step-container">
			<If condition={tabToRender.sidebarStatus !== SidebarStatus.NO_SIDEBAR}>
				<BookingWizardSidebar selectedTrips={selectedTrips} sidebarStatus={tabToRender.sidebarStatus} />
			</If>
			<div className="booking-wizard-step-main-container-horizontal">
				<div className="booking-wizard-step-main-container-vertical">
					<FerryBookingWizardProgressBar
						tabs={tabs}
						progressBarClass={progressBarClass}
						onClickTab={tab => {
							if (!paymentProcessing) {
								navigate(`${baseUrl}/${tab.path}`);
								ScrollToTopOfElement('booking-wizard-step-main-container-horizontal');
							}
							setDisableContinueButton(false);
						}}
						currentTabIndex={currentTabIndex}
						isMobileBookingWizard={isMobileBookingWizard}
						onTimerExpiry={onTimerExpiry}
						ticketsPageUrl={`${baseUrl}/${tabs[1].path}`}
						forEventBooking={false}
						disabled={progressBarLinksDisabled}
					/>
					<div className={containerClass}>
						<div className={classNames('booking-wizard-step-content-background', tabToRender.className)}>
							{/*
							 * In the content flow, not in the progress bar. The bar is `position: fixed`
							 * with a `width: 100%` and its own box-shadow, and the content below only
							 * clears a fixed `$progress-bar-height` — so a banner inside it ran under the
							 * sidebar, cast the bar's shadow over the page, and overlapped the content
							 * whenever it was taller than the 24px the layout assumed. Here it inherits
							 * this element's left/right inset, which is what keeps content off the sidebar.
							 */}
							<If condition={wizardData.restrictedMode}>
								<RestrictedModeBanner pageWithSidebar={false} />
							</If>
							<FerryBookingWizardStepContent
								contentClass={contentClass.length > 0 ? classNames(contentClass) : undefined}
							>
								{tabs[currentTabIndex].component}
							</FerryBookingWizardStepContent>
						</div>
						<If condition={!tabToRender.hideBottomBar}>
							<FerryBookingWizardBottomBar
								disableContinueButton={disableContinueButton}
								tabIsLoading={tabIsLoading}
								continueBtnProps={tabToRender.continueBtnProps}
								continueBtnText={tabToRender.continueBtnText}
								continueBtnLoadingText={tabToRender.continueBtnLoadingText}
								onTimerExpiry={onTimerExpiry}
								onClickContinue={() => {
									if (tabToRender.path === 'payment' || disableBackAndContinueButtons) {
										// PaymentTab will handle everything.
										return;
									}

									if (tabToRender.path === 'add-ons') {
										const newData = new BookingWizardData({ ...wizardData });
										if (
											newData.innerWizardData.type === 'passenger' ||
											newData.innerWizardData.type === 'vehicle'
										) {
											newData.innerWizardData.departingTripOptions.forEach(x => {
												x.adjustedBySystem = false;
											});
											newData.innerWizardData.returningTripOptions.forEach(x => {
												x.adjustedBySystem = false;
											});
											setWizardData(newData);
										}
									}

									// Continuing past Cart is the moment of marketing acknowledgment.
									// Fire-and-forget — a transient failure mustn't block payment.
									if (
										tabs[currentTabIndex].name === 'Cart' &&
										wizardData.userId &&
										wizardData.marketingSubscriptionAcknowledgment !== undefined
									) {
										acknowledgeMarketingPreference(
											wizardData.userId,
											wizardData.marketingSubscriptionAcknowledgment,
										).catch(err => {
											// eslint-disable-next-line no-console
											console.warn('acknowledgeMarketingPreference failed', err);
										});
									}

									// Find will get the first array element which fits the criteria, so we
									// can slice out our non completed steps and find the first one of those
									// which isn't excluded and navigate to that
									const nextStep = tabs
										.slice(currentTabIndex + 1)
										.find(tab => tab.visibility !== VisibilityStatus.EXCLUDED);
									if (tabToRender.validateStep(true)) {
										// If there is no next step then that means that the wizard has been
										// completed
										if (nextStep) {
											const nextStepPath = `${baseUrl}/${nextStep.path}`;
											navigate(!!nextStep ? nextStepPath : '/404');
										}
										ScrollToTopOfElement('booking-wizard-step-main-container-horizontal');
									} else {
										ScrollToError();
									}
								}}
								onClickBack={() => {
									if (disableBackAndContinueButtons) {
										return;
									}

									setDisableContinueButton(false);

									let previousStep = null;
									if (tabs[currentTabIndex].name === 'Cart' && !bookingForAlteration) {
										setOldWizardData(wizardData);
									}
									// For the going backwards, we can't use the same find method because we need the
									// last index instead of the first. We could try to slice out the relevant entries
									// and invert it, but I think this works better because it also allows us to more
									// easily handle what happens when the user presses back from the start of the
									// wizard
									for (let i = currentTabIndex - 1; i >= 0; i--) {
										if (tabs[i].visibility === VisibilityStatus.VISIBLE) {
											previousStep = tabs[i];
											break;
										}
									}
									// If previous step is null here, then the user has gone back from the start of the
									// wizard. If that is the case, then we don't allow the user to navigate
									if (previousStep !== null) {
										navigate(`${baseUrl}/${previousStep.path}`);
										ScrollToTopOfElement('booking-wizard-step-main-container-horizontal');
									}
								}}
								tabs={tabs}
								currentTabIndex={currentTabIndex}
								isIpad={isMobileBookingWizard}
								ticketsPageUrl={`${baseUrl}/${tabs[1].path}`}
							/>
						</If>
					</div>
				</div>
			</div>
		</div>
	);

	return (
		<>
			<If condition={tabToRender.authorize}>
				<AuthStep>{bookingWizardStep}</AuthStep>
			</If>
			<If condition={!tabToRender.authorize}>{bookingWizardStep}</If>
		</>
	);
}

const shouldDisableProgressBarLinks = (disableBackAndContinueButtons: boolean, tabToRender: BookingWizardTab) => {
	if (disableBackAndContinueButtons) {
		return true;
	}
	if (tabToRender.path === 'post-payment') {
		return true;
	}
	if (tabToRender.path === 'login') {
		return true;
	}
	return false;
};
